import React, {PropsWithChildren} from 'react';
import {
    AppGlobalTheme,
    createCachedThemeOptions,
} from '@alchemy/phrasea-framework';
import {Classes} from '../../../classes.ts';
import {FullPageLoader} from '@alchemy/phrasea-ui';
import {useTranslation} from 'react-i18next';
import {useUserPreferencesStore} from '../../../store/userPreferencesStore.ts';
import {useAuth} from '@alchemy/react-auth';
import {updateClientDataLocale} from '../../../store/useDataLocaleStore.ts';
import {scrollbarWidth} from '../../uiVars.ts';
import {useProfileStore} from '../../../store/profileStore.ts';

type Props = PropsWithChildren<{}>;

export default function UserPreferencesProvider({children}: Props) {
    const {t} = useTranslation();
    const {user, isAuthenticated, hasSession} = useAuth();
    const loadingRef = React.useRef(hasSession && !isAuthenticated);

    if (hasSession && !isAuthenticated) {
        loadingRef.current = true;
    }

    const preferences = useUserPreferencesStore(s => s.preferences);
    const applyProfile = useUserPreferencesStore(s => s.applyProfile);
    const loadPreferences = useUserPreferencesStore(s => s.load);
    const loading = useUserPreferencesStore(s => s.loading);
    const loadCurrentProfile = useProfileStore(s => s.loadCurrent);

    const isLoading = loading || loadingRef.current;

    React.useEffect(() => {
        (async () => {
            if (user) {
                loadingRef.current = false;
                const up = await loadPreferences();
                if (up.profile) {
                    const profile = await loadCurrentProfile(up.profile);
                    if (profile) {
                        applyProfile(profile);
                    }
                }
            }
        })();
    }, [loadPreferences, user, loadCurrentProfile, applyProfile]);

    React.useEffect(() => {
        updateClientDataLocale(preferences?.dataLocale);
    }, [preferences?.dataLocale]);

    return (
        <AppGlobalTheme
            scrollbarWidth={scrollbarWidth}
            styles={() => ({
                [`.${Classes.ellipsisText} .MuiListItemText-secondary`]: {
                    textOverflow: 'ellipsis',
                    wordBreak: 'break-all',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                },
            })}
            themeOptions={createCachedThemeOptions(
                preferences.theme ?? 'default'
            )}
        >
            {!isLoading ? (
                children
            ) : (
                <FullPageLoader
                    backdrop={false}
                    message={t(
                        'user_preferences.loading',
                        'Loading user preferences…'
                    )}
                />
            )}
        </AppGlobalTheme>
    );
}
